import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, DrawerContent } from '@/shared/components/ui/Drawer';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { X, Eye, MessageCircle, BookOpen } from 'lucide-react';
import { PraiseWizard } from '@/features/wizard/components/PraiseWizard';
import { FeedbackWizard } from '@/features/wizard/components/FeedbackWizard';
import { DebriefWizard } from '@/features/wizard/components/DebriefWizard';
import type { Interaction } from '@momentum/types';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import {
  debriefWizardFlow,
  praiseWizardFlow,
  feedbackWizardFlow,
} from '@momentum/types';
import { v4 as uuidv4 } from 'uuid';

interface InteractionWizardDrawerProps {
  isOpen: boolean;
  interactionType: 'praise' | 'feedback' | 'debrief' | null;
  goalTitle: string;
  onClose: () => void;
  goalId: string;
  onComplete: (interaction: Interaction) => void;
}

export const InteractionWizardDrawer = ({
  isOpen,
  interactionType,
  goalTitle,
  goalId,
  onClose,
  onComplete,
}: InteractionWizardDrawerProps) => {
  const { t } = useTranslation(['oneOnOne', 'wizard']);
  const { isRTL } = useLanguage();
  const queryClient = useQueryClient();

  // Generate stable flowId that only changes when drawer opens with new interaction type
  // We include isOpen to ensure a fresh wizard session each time the drawer opens
  const flowId = useMemo(() => {
    if (!interactionType) return '';
    return `one-on-one-${interactionType}-${uuidv4()}`;
  }, [interactionType, isOpen]);

  const handleWizardComplete = (interaction?: Interaction) => {
    if (interaction) {
      onComplete(interaction);

      // Invalidate goals cache but don't refetch
      queryClient.invalidateQueries({
        queryKey: ['goals'],
        refetchType: 'none',
      });
    }
    onClose();
  };

  const getIcon = () => {
    switch (interactionType) {
      case 'praise':
        return <Eye className="h-5 w-5 text-emerald-600" />;
      case 'feedback':
        return <MessageCircle className="h-5 w-5 text-amber-600" />;
      case 'debrief':
        return <BookOpen className="h-5 w-5 text-blue-600" />;
      default:
        return null;
    }
  };

  if (!interactionType) return null;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      direction={isRTL ? 'left' : 'right'}
    >
      <DrawerContent className="h-full w-full">
        <div className="sticky top-0 z-10 border-b bg-blue-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-1">
                {getIcon()}
                {t('oneOnOne:oneOnOneInProgress')}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {t('oneOnOne:creatingForGoal')}: <strong>{goalTitle}</strong>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              {t('oneOnOne:skip')}
            </Button>
          </div>
        </div>

        <div className="h-[calc(100vh-5rem)] overflow-y-auto p-6">
          {interactionType === 'praise' ? (
            <PraiseWizard
              onComplete={handleWizardComplete}
              showProgress={false}
              showNavigation={true}
              flowId={flowId}
              autoStart={true}
              firstStepId={praiseWizardFlow.firstStepId}
              initialState={{
                answers: {
                  'goal-selection': goalId,
                },
              }}
              loadingMessage={t('oneOnOne:processing')}
              errorMessage={t('oneOnOne:failedToSave')}
            />
          ) : interactionType === 'feedback' ? (
            <FeedbackWizard
              onComplete={handleWizardComplete}
              showProgress={false}
              showNavigation={true}
              flowId={flowId}
              firstStepId={feedbackWizardFlow.firstStepId}
              initialState={{
                answers: {
                  'goal-selection': goalId,
                },
              }}
              autoStart={true}
              loadingMessage={t('oneOnOne:processing')}
              errorMessage={t('oneOnOne:failedToSave')}
            />
          ) : interactionType === 'debrief' ? (
            <DebriefWizard
              onComplete={handleWizardComplete}
              showProgress={false}
              showNavigation={true}
              flowId={flowId}
              firstStepId={debriefWizardFlow.firstStepId}
              initialState={{
                answers: {
                  'debrief-goal-selection': goalId,
                },
              }}
              loadingMessage={t('oneOnOne:processing')}
              errorMessage={t('oneOnOne:failedToSave')}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">
                {t('oneOnOne:failedToSave')}
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
